import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';
import ValidationGroup from 'devextreme-react/validation-group';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication';
import { Button, ButtonTheme, useWaitOverlayStore } from '@apply/components';
import { Roof } from './components/ExteriorFeatures/components/Roof';
import { Siding } from './components/ExteriorFeatures/components/Siding';
import { Trim } from './components/ExteriorFeatures/components/Trim';
import { GuttersDownspouts } from './components/ExteriorFeatures/components/GuttersDownspouts';
import { PorchDeckPatio } from './components/ExteriorFeatures/components/PorchDeckPatio';
import { GarageOverheadDoor } from './components/ExteriorFeatures/components/GarageOverheadDoor';
import { Doors } from './components/ExteriorFeatures/components/Doors';
import { Windows } from './components/ExteriorFeatures/components/Windows';
import { Columns } from './components/ExteriorFeatures/components/Columns';
import { Shutters } from './components/ExteriorFeatures/components/Shutters';
import { DrivewayAndWalks } from './components/ExteriorFeatures/components/DrivewayAndWalks';
import { Landscaping } from './components/ExteriorFeatures/components/Landscaping';
import { Other } from './components/ExteriorFeatures/components/Other';
import { Flooring } from './components/InteriorFeatures/components/Flooring';
import { Walls } from './components/InteriorFeatures/components/Walls';
import { Ceilings } from './components/InteriorFeatures/components/Ceilings';
import { Trim as InteriorTrim } from './components/InteriorFeatures/components/Trim';
import { Doors as InteriorDoors } from './components/InteriorFeatures/components/Doors';
import { Shelving } from './components/InteriorFeatures/components/Shelving';
import { Cabinets } from './components/InteriorFeatures/components/Cabinets';
import { Lighting } from './components/InteriorFeatures/components/Lighting';
import { Appliances } from './components/InteriorFeatures/components/Appliances';
import { Other as InteriorOther } from './components/InteriorFeatures/components/Other';
import { Kitchen } from './components/AdditionalFeatures/components/Kitchen';
import { Bath } from './components/AdditionalFeatures/components/Bath';
import { EnergySavers } from './components/AdditionalFeatures/components/EnergySavers';
import { QualityAssurance } from './components/AdditionalFeatures/components/QualityAssurance';
import { Other as AdditionalFeaturesOther } from './components/AdditionalFeatures/components/Other';
import { ApplyApi, ISpecSheet } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../ApplicationInsights';
import '../scss/_budgetPage.scss';
import Validator, { CustomRule } from 'devextreme-react/validator';
import DxTextBox from 'devextreme-react/text-box';
import ValidationSummary from 'devextreme-react/validation-summary';
import validationEngine from 'devextreme/ui/validation_engine';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication';
import { budgetAndSpecSheetNameBuilder } from '../constants/budgetAndSpecSheetNameBuilder';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { usePropertyStreet1, usePropertyStreet2 } from '../../queries/usePropertyCoreFields';
import { usePropertySpecSheetFile } from '../../queries/PropertyFileFields/NonRentalFiles';
import { useFormField } from '../../queries/useFormField';
import { useEnsureFormStub } from '../../queries/useEnsureFormStub';
import { useNonRentalSavedFormId } from '../../queries/useNonRentalSavedFormId';
import { useSpecSheetCache } from './queries/useSpecSheetCache';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

interface SpecSheetPageParameters {
    printMode?: boolean;
}

export const SpecSheetPage: React.FC<SpecSheetPageParameters> = ({ printMode = false }) => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const propertyGuid = new URLSearchParams(search).get('pid') || '';

    const token = new URLSearchParams(search).get('token');
    const { isPending } = useApplicationQuery(entryGuid, token);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    useRedirectIfComplete(printMode);

    const applyApi = new ApplyApi(applicationInsights);
    const validationGroupRef = useRef(null);
    const SPEC_SHEET_GROUP = 'spec-sheet-fields';

    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();

    const navigate = useNavigate();

    const specSheet = useSpecSheetCache(propertyGuid);

    const [, setFormName] = useFormField(
        propertyGuid,
        'Spec Sheet',
        (f) => f.name,
        (f, v) => ({ ...f, name: v })
    );

    const ensureFormStub = useEnsureFormStub(propertyGuid, 'Spec Sheet', () => ({
        id: 0,
        guid: entryGuid ?? '',
        type: 'Spec Sheet' as ISpecSheet['type'],
        name: '',
        propertyGuid,
        values: {} as ISpecSheet['values'],
    }));

    const [, setSavedFormId] = useNonRentalSavedFormId(propertyGuid, 'specSheet');

    const { handleMode: specSheetHandleMode } = usePropertySpecSheetFile(propertyGuid);
    const newSpecSheetSelected = specSheetHandleMode === 'createnewform';
    const savedSpecSheetSelected = specSheetHandleMode === 'editsavedform';

    const [propertyName] = usePropertyStreet1(propertyGuid);
    const [propertyName2] = usePropertyStreet2(propertyGuid);

    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.SpecSheet,
        entryGuid,
        propertyGuid,
    });
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    useEffect(() => {
        if (!entryGuid || savedSpecSheetSelected) return;
        ensureFormStub();
    }, [entryGuid, savedSpecSheetSelected]);

    useEffect(() => {
        if (!entryGuid) return;
        setFormName(budgetAndSpecSheetNameBuilder(propertyName ?? '', propertyName2 ?? ''));
    }, [propertyName, propertyName2]);

    useEffect(() => {
        if (printMode) document.body.style.backgroundColor = '#fff';
    }, [printMode]);

    const hasAnySpecValue = (values: Record<string, Record<string, unknown>>): boolean => {
        for (const section of Object.values(values ?? {})) {
            for (const value of Object.values(section ?? {})) {
                if (typeof value === 'string' && value.trim().length > 0) return true;
                if (typeof value === 'number' && Number.isFinite(value) && value !== 0) return true;
                if (typeof value === 'boolean' && value) return true;
            }
        }
        return false;
    };

    const onSaveAndContinue = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        const result = validationEngine.validateGroup(SPEC_SHEET_GROUP);
        if (!result?.isValid) return;

        clearPausePoint();

        const applicationResponse = await validateAndSaveApplication();
        if (!applicationResponse) return;

        if (specSheet) {
            const savedFormResponse = await applyApi.updateSharedForm(specSheet);
            if (savedFormResponse?.result) setSavedFormId(savedFormResponse.result);
        }

        navigateApplicationForward();
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        if (specSheet) {
            const savedFormResponse = await applyApi.updateSharedForm(specSheet);
            if (savedFormResponse?.result) setSavedFormId(savedFormResponse.result);
        }

        navigate('/home');
    };

    const onChangeAutofill = async () => {
        await validateAndSaveApplication();
        navigate(`/selectspecsheet/${entryGuid}?pid=${propertyGuid}`);
    };

    const nextStepButton = {
        id: 'continueApplicationButton',
        dataTestid: 'continueApplicationButton',
        children: 'Continue',
        handleClick: onSaveAndContinue,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
    };

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();
        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    if (isPending && entryGuid) {
        return null;
    }

    return (
        <ValidationGroup
            ref={validationGroupRef}
            className={printMode ? 'print' : ''}
        >
            <ApplicationEntryLayout
                currentCrumb={ApplicationCrumbType.Specs}
                saveAndExitButtonProps={saveAndExitButton}
                nextStepButtonProps={nextStepButton}
                previousStepButtonVisible={true}
                previousStepButtonClick={previousStepButtonClick}
                propertyAddress={propertyName}
                printMode={printMode}
            >
                <div>
                    {!printMode && !newSpecSheetSelected ? (
                        <Button
                            id='tertiaryBudgetButton'
                            theme={ButtonTheme.TertiaryBudget}
                            handleClick={onChangeAutofill}
                            children={
                                <div className='autofill'>
                                    Change autofill selection
                                    <img
                                        src='/assets/img/change-autofill.png'
                                        alt='Change Autofill Selection'
                                        height='100%'
                                    />
                                </div>
                            }
                        ></Button>
                    ) : (
                        <h1>Spec Sheet</h1>
                    )}

                    <div>
                        <h4 className='headers'>Exterior Features</h4>
                        <Roof
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Siding
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Trim
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <GuttersDownspouts
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <PorchDeckPatio
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <GarageOverheadDoor
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Doors
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Windows
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Columns
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Shutters
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <DrivewayAndWalks
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Landscaping
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Other
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                    </div>
                    <div>
                        <h4 className='headers'>Interior Features</h4>
                        <Flooring
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Walls
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Ceilings
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <InteriorTrim
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <InteriorDoors
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Shelving
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Cabinets
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Lighting
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Appliances
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <InteriorOther
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                    </div>
                    <div>
                        <h4 className='headers'>Additional Features</h4>
                        <Kitchen
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <Bath
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <EnergySavers
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <QualityAssurance
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                        <AdditionalFeaturesOther
                            propertyGuid={propertyGuid}
                            isReview={printMode}
                        />
                    </div>
                    <DxTextBox
                        value=''
                        visible={false}
                    >
                        <Validator validationGroup={SPEC_SHEET_GROUP}>
                            <CustomRule
                                reevaluate={true}
                                message='Please fill out at least one value before proceeding'
                                validationCallback={() => hasAnySpecValue(specSheet?.values ?? {})}
                            />
                        </Validator>
                    </DxTextBox>
                    <div className='validation-summary-center'>
                        <ValidationSummary validationGroup={SPEC_SHEET_GROUP} />
                    </div>
                </div>
            </ApplicationEntryLayout>
        </ValidationGroup>
    );
};
